import React, { useState } from 'react';
import { useComplaintsStore } from '../../store/complaintsStore';
import { type Complaint } from '../../services/complaintsService';

interface ComplaintReplyFormProps {
  complaint: Complaint;
  onClose: () => void;
  onSuccess: () => void;
}

export function ComplaintReplyForm({ complaint, onClose, onSuccess }: ComplaintReplyFormProps) {
  const [reply, setReply] = useState('');
  const [statusChange, setStatusChange] = useState<'in_progress' | 'closed' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { replyToComplaint, isSubmitting: storeIsSubmitting } = useComplaintsStore();

  // Форматирование даты
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Обработка отправки ответа
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reply.trim()) {
      setError('Поле ответа обязательно для заполнения');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await replyToComplaint(complaint.id, reply.trim(), statusChange || undefined);
      onSuccess();
    } catch (error) {
      console.error('Ошибка отправки ответа:', error);
      setError('Не удалось отправить ответ. Попробуйте еще раз.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Обработка отмены
  const handleCancel = () => {
    setReply('');
    setStatusChange(null);
    setError(null);
    onClose();
  };

  const isLoading = isSubmitting || storeIsSubmitting;

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-6">
      {/* Заголовок и кнопка назад */}
      <div className="flex items-center justify-between">
        <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[32px] text-left text-nowrap">
          <p className="block leading-[40px] whitespace-pre">Ответить на жалобу #{complaint.id}</p>
        </div>
        <button
          onClick={handleCancel}
          className="transition-colors text-[15px] font-medium"
          style={{ color: 'var(--brand-primary, #e16349)' }}
        >
          ← Назад к списку
        </button>
      </div>
      
      {/* Информация о жалобе */}
      <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full p-[24px] border border-[#e2e4e9]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
            <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
              <p className="block leading-[20px]">Автор</p>
            </label>
            <div className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">{complaint.author}</div>
          </div>
          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
            <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
              <p className="block leading-[20px]">Email</p>
            </label>
            <div className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">{complaint.authorEmail}</div>
          </div>
          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
            <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
              <p className="block leading-[20px]">Дата</p>
            </label>
            <div className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">{formatDate(complaint.createdAt)}</div>
          </div>
          <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
            <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
              <p className="block leading-[20px]">Тип</p>
            </label>
            <div className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">{complaint.type}</div>
          </div>
        </div>
      </div>

      {/* Описание жалобы */}
      <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
        <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
          <p className="block leading-[20px]">Описание жалобы</p>
        </label>
        <div className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full p-[24px] border border-[#e2e4e9] min-h-[100px]">
          <div className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif] whitespace-pre-wrap">
            {complaint.description}
          </div>
        </div>
      </div>

      {/* Форма ответа */}
      <form onSubmit={handleSubmit} className="box-border content-stretch flex flex-col gap-6 items-start justify-start p-0 relative shrink-0 w-full">
        {/* Поле ответа */}
        <div className="box-border content-stretch flex flex-col gap-2 items-start justify-start p-0 relative shrink-0 w-full">
          <label htmlFor="reply" className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
            <p className="block leading-[20px]">Ваш ответ *</p>
          </label>
          <textarea
            id="reply"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={6}
            className="bg-[#ffffff] relative rounded-3xl shrink-0 w-full py-[16px] px-[24px] border border-[#e2e4e9] focus:outline-none text-[14px] font-['Inter:Regular',_sans-serif] text-[#525866] placeholder:text-[#868c98] resize-none"
            placeholder="Введите ваш ответ на жалобу..."
            disabled={isLoading}
            required
            onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary, #e16349)'; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = '#e2e4e9'; }}
          />
        </div>

        {/* Опции изменения статуса */}
        <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-0 relative shrink-0 w-full">
          <label className="css-7dow9l font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[#000000] text-[14px] text-left w-full">
            <p className="block leading-[20px]">Изменить статус</p>
          </label>
          <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-0 relative shrink-0 w-full">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="statusChange"
                value="in_progress"
                checked={statusChange === 'in_progress'}
                onChange={(e) => setStatusChange(e.target.value as 'in_progress')}
                className="w-4 h-4 text-[#e16349] border-[#e2e4e9] focus:ring-[#e16349]"
                disabled={isLoading}
              />
              <span className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">В работе</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="statusChange"
                value="closed"
                checked={statusChange === 'closed'}
                onChange={(e) => setStatusChange(e.target.value as 'closed')}
                className="w-4 h-4 text-[#e16349] border-[#e2e4e9] focus:ring-[#e16349]"
                disabled={isLoading}
              />
              <span className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">Закрыта</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="statusChange"
                value=""
                checked={statusChange === null}
                onChange={() => setStatusChange(null)}
                className="w-4 h-4 text-[#e16349] border-[#e2e4e9] focus:ring-[#e16349]"
                disabled={isLoading}
              />
              <span className="text-[#525866] text-[14px] font-['Inter:Regular',_sans-serif]">Не изменять</span>
            </label>
          </div>
        </div>

        {/* Ошибка */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-3xl p-4 w-full">
            <p className="text-red-600 text-[14px] font-['Inter:Regular',_sans-serif]">{error}</p>
          </div>
        )}

        {/* Кнопки */}
        <div className="box-border content-stretch flex flex-row gap-3 items-center justify-end p-0 relative shrink-0 w-full">
          <button
            type="submit"
            disabled={isLoading || !reply.trim()}
            className="text-white p-[16px] h-[52px] rounded-3xl transition-colors font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-center tracking-[-0.176px] px-[24px] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: 'var(--brand-primary, #e16349)' }}
          >
            {isLoading ? 'Сохранение...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
}
